import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "components/ui/pagination";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useState } from "react";
import { ArticleFeedRow } from "./article-feed-row";
import { useFetchArticles } from "./fetch-hooks";
import { Skeleton } from "components/ui/skeleton";

interface ArticlesListProps {
  limit?: number; // If provided, only show this many articles (no pagination)
  onArticleClick?: (identifier: string) => void;
  // Divide the parent's height between the rows, for the dashboard tile sized by
  // its grid cell. Left off, rows are a fixed height and the list grows with its
  // content, for the full page.
  fill?: boolean;
}

export const ArticlesList = ({
  limit,
  onArticleClick,
  fill = false,
}: ArticlesListProps) => {
  const [page, setPage] = useState(1);
  const pageSize = limit || 10;

  const articlesQuery = useFetchArticles(page, pageSize);

  const isLoading = articlesQuery.isPending;
  const isSuccess = articlesQuery.isSuccess;

  const rootClass = fill
    ? "flex min-h-0 flex-1 flex-col gap-4 w-full"
    : "flex flex-col gap-4 w-full";

  // `@container` makes the rows respond to the list's own width rather than the
  // viewport, which is what the dashboard's resizable grid cell needs.
  const listClass = fill
    ? "@container flex min-h-0 flex-1 flex-col gap-3"
    : "@container flex flex-col gap-3";

  const rowClass = fill ? "min-h-0 flex-1" : "h-28";

  return (
    <div className={rootClass}>
      <ReactQueryErrorWrapper queries={[articlesQuery]} mutations={[]} />

      {/* Loading Skeletons */}
      {isLoading && (
        <div className={listClass}>
          {Array.from({ length: limit || 6 }).map((_, i) => (
            <Skeleton key={i} className={`${rowClass} rounded-md`} />
          ))}
        </div>
      )}

      {/* Articles */}
      {isSuccess && (
        <>
          <div className={listClass} data-testid="articles-list">
            {articlesQuery.data.data.map((article) => (
              <ArticleFeedRow
                key={article.id}
                article={article}
                className={rowClass}
                onClick={
                  onArticleClick
                    ? () => onArticleClick(article.slug || article.documentId)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Pagination - only show if not limited */}
          {!limit && articlesQuery.data.meta.pagination.pageCount > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={
                      page <= 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of{" "}
                    {articlesQuery.data.meta.pagination.pageCount}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setPage((p) =>
                        Math.min(
                          articlesQuery.data.meta.pagination.pageCount,
                          p + 1,
                        ),
                      );
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={
                      page >= articlesQuery.data.meta.pagination.pageCount
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
};
